// 🔍 全局参数监控工具
// 用于观察应用中的参数传递频率，判断是否存在流式渲染

class ParameterMonitor {
  constructor() {
    this.stats = {
      // API统计
      apiCalls: 0,
      apiCallsPerSecond: 0,
      lastApiTime: Date.now(),

      // 事件统计
      events: 0,
      eventsPerSecond: 0,
      lastEventTime: Date.now(),

      // 渲染统计
      renders: 0,
      rendersPerSecond: 0,
      lastRenderTime: Date.now(),

      // 流式数据统计
      streamingActive: false,
      streamingStartTime: null,
      streamingDuration: 0,

      // 总体活动统计
      totalActivity: 0,
      activityStartTime: Date.now(),
    };

    this.thresholds = {
      highFrequencyApi: 5, // 每秒超过5次API调用认为是高频
      highFrequencyEvent: 30, // 每秒超过30次事件认为是高频
      streamingIndicator: 10, // 每秒超过10次活动认为可能是流式渲染
    };

    this.init();
  }

  init() {
    // 每秒统计一次
    setInterval(() => {
      this.calculateRates();
      this.checkStreamingActivity();
      this.logSummary();
    }, 1000);

    // 每10秒重置计数器
    setInterval(() => {
      this.resetCounters();
    }, 10000);

    console.log("🔍 参数监控器已启动");
  }

  // 记录API调用
  recordApiCall(details = {}) {
    this.stats.apiCalls++;
    this.stats.totalActivity++;
    this.stats.lastApiTime = Date.now();
    // 关闭API调用日志
  }

  // 记录事件
  recordEvent(eventType, details = {}) {
    this.stats.events++;
    this.stats.totalActivity++;
    this.stats.lastEventTime = Date.now();
    // 关闭事件日志
  }

  // 记录渲染
  recordRender(component, details = {}) {
    this.stats.renders++;
    this.stats.totalActivity++;
    this.stats.lastRenderTime = Date.now();
    // 关闭渲染日志
  }

  // 标记流式数据开始
  startStreaming(source = "unknown") {
    this.stats.streamingActive = true;
    this.stats.streamingStartTime = Date.now();

    console.log(`🌊 流式数据开始 (${source}):`, {
      timestamp: new Date().toLocaleTimeString(),
    });
  }

  // 标记流式数据结束
  stopStreaming(source = "unknown") {
    if (this.stats.streamingActive) {
      this.stats.streamingDuration = Date.now() - this.stats.streamingStartTime;
      this.stats.streamingActive = false;

      console.log(`🏁 流式数据结束 (${source}):`, {
        timestamp: new Date().toLocaleTimeString(),
        duration: `${this.stats.streamingDuration}ms`,
      });
    }
  }

  // 计算频率
  calculateRates() {
    const now = Date.now();
    const timeDiff = (now - this.stats.activityStartTime) / 1000;

    if (timeDiff > 0) {
      this.stats.apiCallsPerSecond =
        Math.round((this.stats.apiCalls / timeDiff) * 10) / 10;
      this.stats.eventsPerSecond =
        Math.round((this.stats.events / timeDiff) * 10) / 10;
      this.stats.rendersPerSecond =
        Math.round((this.stats.renders / timeDiff) * 10) / 10;
    }
  }

  // 检查流式活动
  checkStreamingActivity() {
    const totalRate =
      this.stats.apiCallsPerSecond +
      this.stats.eventsPerSecond +
      this.stats.rendersPerSecond;

    if (totalRate > this.thresholds.streamingIndicator) {
      console.log(`🌊 检测到可能的流式渲染活动:`, {
        totalRate: `${totalRate}/秒`,
        apiRate: `${this.stats.apiCallsPerSecond}/秒`,
        eventRate: `${this.stats.eventsPerSecond}/秒`,
        renderRate: `${this.stats.rendersPerSecond}/秒`,
        isStreaming: this.stats.streamingActive,
      });
    }
  }

  // 打印摘要
  logSummary() {
    // 关闭活动摘要日志
  }

  // 判断是否为高活动状态
  isHighActivity() {
    return (
      this.stats.apiCallsPerSecond > this.thresholds.highFrequencyApi ||
      this.stats.eventsPerSecond > this.thresholds.highFrequencyEvent ||
      this.stats.streamingActive
    );
  }

  // 重置计数器
  resetCounters() {
    this.stats.apiCalls = 0;
    this.stats.events = 0;
    this.stats.renders = 0;
    this.stats.totalActivity = 0;
    this.stats.activityStartTime = Date.now();
  }

  // 获取当前状态
  getStatus() {
    return {
      ...this.stats,
      isHighActivity: this.isHighActivity(),
      timestamp: new Date().toLocaleTimeString(),
    };
  }
}

// 创建全局实例
const parameterMonitor = new ParameterMonitor();

// 导出实例和便捷方法
export default parameterMonitor;

export const recordApiCall = (details) =>
  parameterMonitor.recordApiCall(details);
export const recordEvent = (eventType, details) =>
  parameterMonitor.recordEvent(eventType, details);
export const recordRender = (component, details) =>
  parameterMonitor.recordRender(component, details);
export const startStreaming = (source) =>
  parameterMonitor.startStreaming(source);
export const stopStreaming = (source) => parameterMonitor.stopStreaming(source);
export const getMonitorStatus = () => parameterMonitor.getStatus();

// 在window对象上暴露监控器，方便调试
if (typeof window !== "undefined") {
  window.parameterMonitor = parameterMonitor;
  window.getMonitorStatus = getMonitorStatus;
}
